import { useState, useEffect, useRef, useCallback } from 'react'
import { type Category } from '@/lib/constants'
import { CategoryButton } from './category-button'
import { useRecords } from '@/contexts/record-context'
import { useTimer } from '@/hooks/use-timer'
import { useNotification } from '@/hooks/use-notification'
import { RecordForm } from './record-form'

import type { TimerSnapshot } from '@/store/storage'

export function CategoryPanel() {
  const { activeTimer, startTimer, stopTimer, restoreTimer, fetchTodayRecords } = useRecords()
  const { display } = useTimer()
  const { requestPermission } = useNotification()
  const [completedCategory, setCompletedCategory] = useState<Category | null>(null)
  const [completedDuration, setCompletedDuration] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const timerSnapshotRef = useRef<TimerSnapshot | null>(null)

  useEffect(() => {
    if (completedCategory) {
      const timer = setTimeout(() => setCompletedCategory(null), 2000)
      return () => clearTimeout(timer)
    }
  }, [completedCategory])

  const handleClick = async (category: Category) => {
    if (activeTimer && activeTimer.category === category) {
      // Save snapshot before showing form, so we can restore if user skips
      timerSnapshotRef.current = { category: activeTimer.category, startTime: activeTimer.startTime }
      const elapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
      setCompletedDuration(elapsed)
      setCompletedCategory(category)
      setShowForm(true)
      await requestPermission()
    } else if (activeTimer) {
      // Already recording another category, stop it first then start new one
      await stopTimer({})
      await startTimer(category)
      await requestPermission()
    } else {
      // Start recording
      await startTimer(category)
      await requestPermission()
    }
  }

  const handleFormSubmit = useCallback(async (details: Record<string, string>): Promise<unknown> => {
    const snapshot = timerSnapshotRef.current
    if (!snapshot) return null
    timerSnapshotRef.current = null

    // Stop timer and save record (stopTimer handles both online/offline)
    const record = await stopTimer(details)
    setShowForm(false)
    // Record is already added to state via dispatch in stopTimer
    // Optionally refresh today records to ensure consistency
    await fetchTodayRecords()
    return record
  }, [stopTimer, fetchTodayRecords])

  const handleFormClose = useCallback(() => {
    // User skipped/closed - restore timer to continue counting
    if (timerSnapshotRef.current) {
      restoreTimer(timerSnapshotRef.current)
    }
    timerSnapshotRef.current = null
    setShowForm(false)
  }, [restoreTimer])

  const categories: Category[] = ['sleep', 'exercise', 'reading', 'work', 'drunk', 'meditation']

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        {categories.map((cat) => {
          const isActive = activeTimer?.category === cat && !showForm
          const isCompleted = completedCategory === cat && !showForm
          const disabled = (activeTimer && activeTimer.category !== cat) || showForm
          return (
            <CategoryButton
              key={cat}
              category={cat}
              isActive={isActive}
              isCompleted={isCompleted}
              disabled={disabled}
              elapsedDisplay={activeTimer?.category === cat ? display : undefined}
              onClick={() => handleClick(cat)}
            />
          )
        })}
      </div>

      {showForm && activeTimer && (
        <RecordForm
          category={activeTimer.category as Category}
          duration={completedDuration}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
        />
      )}
    </>
  )
}
